/**
 * The site's words, in the app's languages, each written as its readers would say it (docs/COPYWRITING.md; the
 * app's glossary: "assinatura", "plano", "Revisa" without an article in Italian).
 */
import { legalUrls } from "../../web/src/lib/legal.ts";

export type Lang = "en" | "pt-BR" | "it";

/** The site's languages, as the language picker offers them (each named in itself). */
export const LANGS: readonly { lang: Lang; name: string }[] = [
  { lang: "en", name: "English" },
  { lang: "pt-BR", name: "Português (Brasil)" },
  { lang: "it", name: "Italiano" },
];

/** A state of the page: its title and what it says. */
interface Said {
  title: string;
  body: string;
}

export type State =
  | "checkout"
  | "paying"
  | "manage"
  | "paid"
  | "cancelled"
  | "failed"
  | "notSold"
  | "noSubscription"
  | "badLink";

export type Strings = Record<State, Said> & {
  back: string;
  again: string;
  language: string;
  /** What `failed` adds when the checkout says why. */
  why: string;
  /** Before the payment provider's logo, and before the subscription provider's. */
  payments: string;
  subscriptions: string;
  legal: string;
  terms: string;
  privacy: string;
};

const SUBTAG = /[-_]/u;

const STRINGS: Record<Lang, Strings> = {
  en: {
    checkout: { title: "Opening the checkout…", body: "Just a moment." },
    paying: {
      title: "Complete your purchase",
      body: "Enter your payment details in the checkout. Nothing is charged until you confirm.",
    },
    manage: {
      title: "Opening your subscription…",
      body: "Its page lets you change the plan, update the payment method, see invoices or cancel.",
    },
    paid: { title: "Payment received", body: "Taking you back to Revisa, where your plan shows in a moment." },
    cancelled: { title: "Checkout cancelled", body: "Nothing was charged. Choose a plan again whenever you like." },
    failed: {
      title: "The payment didn't go through",
      body: "Try again, or write to contato@prelude.ltda if it keeps happening.",
    },
    notSold: { title: "This plan isn't on sale", body: "Choose another one on the Plan page in Revisa." },
    noSubscription: {
      title: "No subscription to manage",
      body: "This organization has no subscription yet. Choose a plan on the Plan page in Revisa.",
    },
    badLink: { title: "This link isn't valid", body: "Start again from the Plan page in Revisa." },
    back: "Back to Revisa",
    again: "Try again",
    language: "Language",
    why: "The checkout said: {error}",
    payments: "Secure payment by",
    subscriptions: "Subscriptions by",
    legal: "Legal",
    terms: "Terms of service",
    privacy: "Privacy policy",
  },
  "pt-BR": {
    checkout: { title: "Abrindo o pagamento…", body: "Só um instante." },
    paying: {
      title: "Conclua sua assinatura",
      body: "Preencha os dados de pagamento. Nada é cobrado até você confirmar.",
    },
    manage: {
      title: "Abrindo sua assinatura…",
      body: "Na página dela você troca de plano, atualiza a forma de pagamento, vê as faturas ou cancela.",
    },
    paid: { title: "Pagamento recebido", body: "Voltando ao Revisa, onde seu plano aparece em instantes." },
    cancelled: {
      title: "Pagamento cancelado",
      body: "Nada foi cobrado. Você pode escolher um plano de novo quando quiser.",
    },
    failed: {
      title: "O pagamento não foi concluído",
      body: "Tente de novo ou, se continuar acontecendo, escreva para contato@prelude.ltda.",
    },
    notSold: { title: "Este plano não está à venda", body: "Escolha outro na página Plano do Revisa." },
    noSubscription: {
      title: "Nenhuma assinatura para gerenciar",
      body: "Esta organização ainda não tem assinatura. Escolha um plano na página Plano do Revisa.",
    },
    badLink: { title: "Este link não é válido", body: "Comece de novo pela página Plano do Revisa." },
    back: "Voltar ao Revisa",
    again: "Tentar de novo",
    language: "Idioma",
    why: "Motivo informado: {error}",
    payments: "Pagamento seguro com",
    subscriptions: "Assinaturas com",
    legal: "Documentos legais",
    terms: "Termos de uso",
    privacy: "Política de privacidade",
  },
  it: {
    checkout: { title: "Apertura del pagamento…", body: "Solo un attimo." },
    paying: {
      title: "Completa l'acquisto",
      body: "Inserisci i dati di pagamento. Non viene addebitato nulla finché non confermi.",
    },
    manage: {
      title: "Apertura del tuo abbonamento…",
      body: "Dalla sua pagina puoi cambiare piano, aggiornare il metodo di pagamento, vedere le fatture o disdire.",
    },
    paid: { title: "Pagamento ricevuto", body: "Ti riportiamo su Revisa, dove il tuo piano comparirà tra poco." },
    cancelled: {
      title: "Pagamento annullato",
      body: "Non è stato addebitato nulla. Puoi scegliere un piano quando vuoi.",
    },
    failed: {
      title: "Il pagamento non è andato a buon fine",
      body: "Riprova oppure, se succede ancora, scrivi a contato@prelude.ltda.",
    },
    notSold: { title: "Questo piano non è in vendita", body: "Scegline un altro nella pagina Piano di Revisa." },
    noSubscription: {
      title: "Nessun abbonamento da gestire",
      body: "Questa organizzazione non ha ancora un abbonamento. Scegli un piano nella pagina Piano di Revisa.",
    },
    badLink: { title: "Questo link non è valido", body: "Ricomincia dalla pagina Piano di Revisa." },
    back: "Torna a Revisa",
    again: "Riprova",
    language: "Lingua",
    why: "Motivo indicato: {error}",
    payments: "Pagamento sicuro con",
    subscriptions: "Abbonamenti con",
    legal: "Note legali",
    terms: "Termini di servizio",
    privacy: "Informativa sulla privacy",
  },
};

/**
 * RevenueCat's checkout in Brazilian Portuguese: its own `pt` words are Portugal's ("Subscrever", "subscrição",
 * "faturação"), so the lines it shows are given as Brazilians say them; and a few Italian ones (a subscription is
 * "disdetto"). By the SDK's locale key (`pt_BR` falls back to `pt`); see pay.ts `labelsOverride`.
 */
export const CHECKOUT_LABELS: Readonly<Record<"pt" | "it", Readonly<Record<string, string>>>> = {
  pt: {
    "product_info.subscribe_to": "Assinar",
    "product_info.cancel_anytime": "Cancele quando quiser",
    "payment_entry_page.button_start_trial": "Começar o teste",
    "payment_entry_page.subscription_info": "A assinatura é renovada automaticamente. Cancele quando quiser.",
    "payment_entry_page.subscription_terms_info":
      "Ao assinar, você autoriza o {{appName}} a fazer as cobranças de acordo com os termos dele até você cancelar.",
    "payment_entry_page.trial_subscription_terms_info":
      "Ao fim do período de teste, você será cobrado {{price}} {{perFrequency}} a partir de {{renewalDate}}. Cancele quando quiser antes dessa data.",
    "payment_entry_page.otp_terms_info":
      "Ao comprar, você autoriza o {{appName}} a cobrar de acordo com os termos dele.",
    "payment_entry_page.checkout_consent_terms_of_service": "Termos de uso",
    "payment_entry_page.payment_method_error":
      "Não foi possível usar esta forma de pagamento. Confira os dados ou tente outra forma de pagamento.",
    "error_page.error_title_already_subscribed": "Você já assina {{productTitle}}",
    "error_page.error_message_already_subscribed": "Você não pode assinar este produto de novo.",
    "error_page.error_title_stripe_missing_required_permission": "Falta uma permissão do Stripe",
    "price_update.trial_message":
      "O preço da assinatura foi atualizado com os impostos do seu endereço de cobrança. O período de teste grátis continua valendo. Confira e tente de novo.",
    "upgrade_confirm_page.on_file": "Cadastrado",
  },
  it: {
    "product_info.cancel_anytime": "Disdici quando vuoi",
    "payment_entry_page.subscription_info": "L'abbonamento si rinnova automaticamente. Puoi disdirlo quando vuoi.",
    "payment_entry_page.subscription_terms_info":
      "Abbonandoti, accetti che {{appName}} ti addebiti l'importo secondo i suoi termini fino alla disdetta.",
  },
};

/** The language asked for (`pt`, `pt-BR`, `it-IT`…), English when it isn't one of the app's. */
export function langOf(tag: string | null): Lang {
  const code = (tag ?? "").toLowerCase().split(SUBTAG)[0];
  return code === "pt" ? "pt-BR" : code === "it" ? "it" : "en";
}

export const stringsOf = (lang: Lang): Strings => STRINGS[lang];

/** The Terms of service on the site, in `lang` (linked from the checkout and the page's footer). */
export const termsUrl = (lang: Lang): string => legalUrls(lang).terms;

/** The Privacy policy on the site, in `lang`. */
export const privacyUrl = (lang: Lang): string => legalUrls(lang).privacy;
